import { zodResolver } from "@hookform/resolvers/zod";
import { startOfDay } from "date-fns";
import dayjs from "dayjs";
import { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useCreateIdentificationDocumentMutation } from "web_myplann/Api/Client/useCreateIdentificationDocumentMutation";
import { useUpdateIdentificationDocumentMutation } from "web_myplann/Api/Client/useUpdateIdentificationDocumentMutation";
import { useIdentificationDocumentReminderCreateMutation } from "web_myplann/Api/Reminders/useIdentificationDocumentReminderCreateMutation";
import {
	useDocumentsFeatureOptional,
	useRemindersRightOptional,
} from "web_myplann/Api/Rights/RightsProvider";
import {
	ClientIdentificationDocumentTypeEnum,
	type IdentificationDocumentListItemResponseDTO,
	ReminderEntityTypeEnum,
} from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import type { PaperworkDocType } from "web_myplann/Profile/Constants/paperworkDocTypes";
import { getReminderEntityConfig } from "web_myplann/Reminders/Config/reminderEntityConfig";
import {
	type ReminderDraft,
	resolveReminderDrafts,
} from "web_myplann/Reminders/Utils/reminderDrafts";
import type { TranslateFunction } from "web_myplann/Shared/TranslateFunction";
import { z } from "zod";

const paperworkFormObjectSchema = z.object({
	customName: z.string(),
	number: z.string(),
	validTo: z.date().nullable(),
	noExpiry: z.boolean(),
});

export type PaperworkFormValues = z.infer<typeof paperworkFormObjectSchema>;

const getPaperworkFormSchema = (t: TranslateFunction, isCustom: boolean) => {
	const requiredMessage = t(Resources.Form.Validations.required);

	return paperworkFormObjectSchema.superRefine((data, ctx) => {
		if (isCustom && data.customName.trim().length === 0) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ["customName"],
				message: requiredMessage,
			});
		}

		if (!data.noExpiry && data.validTo == null) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ["validTo"],
				message: requiredMessage,
			});
		}
	});
};

export type PaperworkSubmitResult = { reminderSet: boolean };

const REMINDER_ENTITY_CONFIG = getReminderEntityConfig(
	ReminderEntityTypeEnum.IdentificationDocument,
);

export const usePaperworkDocumentForm = (
	type: PaperworkDocType,
	document?: IdentificationDocumentListItemResponseDTO,
) => {
	const { t } = useResource();
	const isUpdate = document != null;
	const form = useForm<PaperworkFormValues>({
		resolver: zodResolver(getPaperworkFormSchema(t, type.isCustom === true)),
		defaultValues: {
			customName: document?.name ?? "",
			number: document?.number ?? "",
			validTo: document?.validTo ? dayjs(document.validTo).toDate() : null,
			noExpiry: document
				? document.validTo == null
				: type.id === ClientIdentificationDocumentTypeEnum.Other,
		},
	});

	const hasRemindersRight = useRemindersRightOptional();
	const isDocumentsFeatureEnabled = useDocumentsFeatureOptional();
	const isRemindersEnabled = hasRemindersRight && isDocumentsFeatureEnabled;

	const [reminderDrafts, setReminderDrafts] = useState<ReminderDraft[]>([]);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const noExpiry = form.watch("noExpiry");
	const validTo = form.watch("validTo");

	const createDocument = useCreateIdentificationDocumentMutation();
	const updateDocument = useUpdateIdentificationDocumentMutation();
	const createReminder = useIdentificationDocumentReminderCreateMutation();

	const submittingRef = useRef(false);
	const documentIdRef = useRef<number | null>(document?.id ?? null);
	const persistedReminderIdsRef = useRef<Set<string>>(new Set());
	const [persistedReminderIds, setPersistedReminderIds] = useState<
		ReadonlySet<string>
	>(new Set());

	const persistDocument = async (
		values: PaperworkFormValues,
		onSuccess: (result: PaperworkSubmitResult) => void,
	) => {
		if (submittingRef.current) return;
		submittingRef.current = true;
		setIsSubmitting(true);

		try {
			const requestData = {
				documentType: type.id,
				name: type.isCustom ? values.customName || null : null,
				number: type.isCustom ? null : values.number || null,
				validTo:
					values.noExpiry || !values.validTo
						? null
						: dayjs(values.validTo).format("YYYY-MM-DDTHH:mm:ssZ"),
			};

			let documentId = documentIdRef.current;
			if (documentId === null) {
				const created = await createDocument.mutateAsync(requestData);
				documentId = created.id;
				documentIdRef.current = documentId;
			} else {
				await updateDocument.mutateAsync({ id: documentId, data: requestData });
			}

			if (!isUpdate && isRemindersEnabled && !values.noExpiry) {
				const pendingReminders = resolveReminderDrafts(
					reminderDrafts,
					REMINDER_ENTITY_CONFIG,
					values.validTo,
					startOfDay(new Date()),
				).filter((draft) => !persistedReminderIdsRef.current.has(draft.id));

				for (const draft of pendingReminders) {
					await createReminder.mutateAsync({
						documentId,
						model: draft.model,
					});
					persistedReminderIdsRef.current.add(draft.id);
					setPersistedReminderIds(new Set(persistedReminderIdsRef.current));
				}
			}

			onSuccess({ reminderSet: persistedReminderIdsRef.current.size > 0 });
		} catch {
			// The global mutation cache (see Infrastructure/App.tsx) already surfaces
			// a toast for the failure — stay on the form so the user can retry.
		} finally {
			submittingRef.current = false;
			setIsSubmitting(false);
		}
	};

	const submit = async (onSuccess: (result: PaperworkSubmitResult) => void) => {
		await form.handleSubmit((values) => persistDocument(values, onSuccess))();
	};

	return {
		control: form.control,
		noExpiry,
		setNoExpiry: (value: boolean) => form.setValue("noExpiry", value),
		validTo,
		documentId: document?.id,
		isRemindersEnabled,
		reminderDrafts,
		setReminderDrafts,
		persistedReminderIds,
		isSubmitting,
		submit,
	};
};
